import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, RotateCcw, PawPrint, ShieldCheck, Wind, HeartPulse } from "lucide-react";
import { useLang } from "../../i18n";
import { preRideSections } from "../../data/safety";

const icons = { PawPrint, ShieldCheck, Wind, HeartPulse };
const STORAGE = "coi_preride_v1";

export default function PreRideCheck() {
  const { t, lang } = useLang();
  const allIds = preRideSections.flatMap((s) => s.items.map((i) => i.id));
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE)) || []; } catch { return []; }
  });

  useEffect(() => { localStorage.setItem(STORAGE, JSON.stringify(checked)); }, [checked]);

  const toggle = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const reset = () => setChecked([]);

  const done = checked.length;
  const total = allIds.length;
  const pct = Math.round((done / total) * 100);
  const ready = done === total;

  return (
    <div>
      <p className="text-sm text-wachs-dim leading-relaxed max-w-2xl">{t.safety.preride.intro}</p>

      {/* Status panel */}
      <div className={`mt-6 grid-panel rounded-lg p-5 flex items-center justify-between gap-4 ${ready ? "border-ready/50" : ""}`}>
        <div className="flex-1">
          <div className="flex items-center justify-between instrument-label text-wachs-dim">
            <span>{t.safety.preride.progress}</span>
            <span className="font-mono">{done}/{total}</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-nacht overflow-hidden">
            <motion.div
              className={ready ? "h-full bg-ready" : "h-full bg-sonne"}
              animate={{ width: `${pct}%` }}
              transition={{ ease: "easeOut", duration: 0.4 }}
            />
          </div>
        </div>
        <div
          data-testid="preride-status"
          className={`shrink-0 font-mono text-sm px-4 py-2 rounded-md border ${
            ready ? "text-ready border-ready/50 bg-ready/10 pulse-ready" : "text-sonne border-sonne/40 bg-sonne/10"
          }`}
        >
          {ready ? `● ${t.safety.preride.ready}` : `${total - done} ${t.safety.preride.remaining}`}
        </div>
      </div>

      {ready && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-md border border-ready/40 bg-ready/10 p-4 text-sm text-ready" data-testid="preride-ready-msg">
          {t.safety.preride.readyMsg}
        </motion.div>
      )}

      {/* Sections */}
      <div className="mt-8 grid gap-5 md:grid-cols-2">
        {preRideSections.map((section) => {
          const Icon = icons[section.icon];
          const secDone = section.items.filter((i) => checked.includes(i.id)).length;
          return (
            <div key={section.id} className="grid-panel rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-bronze/15 bg-tiefsee-2/40">
                <div className="flex items-center gap-2.5">
                  <Icon size={18} className="text-sonne" />
                  <span className="font-mono text-sm tracking-wide">{lang === "de" ? section.de : section.en}</span>
                </div>
                <span className="font-mono text-xs text-wachs-dim">{secDone}/{section.items.length}</span>
              </div>
              <ul className="divide-y divide-bronze/10">
                {section.items.map((item) => {
                  const on = checked.includes(item.id);
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => toggle(item.id)}
                        data-testid={`preride-item-${item.id}`}
                        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-tiefsee-2/40 transition-colors"
                      >
                        <span className={`mt-0.5 h-5 w-5 shrink-0 rounded border flex items-center justify-center transition-colors ${on ? "bg-ready border-ready" : "border-wachs-dim/40"}`}>
                          {on && <Check size={14} className="text-nacht" strokeWidth={3} />}
                        </span>
                        <span className={`text-sm leading-snug ${on ? "text-wachs-dim line-through" : "text-wachs"}`}>
                          {lang === "de" ? item.de : item.en}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <button onClick={reset} data-testid="preride-reset" className="mt-6 inline-flex items-center gap-2 text-sm text-wachs-dim hover:text-sonne transition-colors">
        <RotateCcw size={15} /> {t.safety.preride.reset}
      </button>
    </div>
  );
}
